import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useFormik } from "formik";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import moment from "moment";
import * as Yup from "yup";
import { getTypeCustomers, addCustomerList } from "helpers/fakebackend_helper";

const CustomerForm = forwardRef(({ onSuccess, setCustomerId }: { onSuccess?: () => void; setCustomerId: (id: number) => void }, ref) => {
  const FlatpickrComponent = Flatpickr as unknown as React.ComponentType<any>;

  const [typeCustomerOptions, setTypeCustomerOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useImperativeHandle(ref, () => ({
    submitForm: () => validation.handleSubmit(),
    resetForm: () => validation.resetForm(),
  }));

  useEffect(() => {
    const fetchTypeCustomers = async () => {
      try {
        const response = await getTypeCustomers();
        const embedded = response.data?._embedded?.typeCustomers;
        const options = embedded
          ? Object.values(embedded).map((typeCustomer: any) => ({
              value: typeCustomer._links.self.href,
              label: typeCustomer.name,
            }))
          : [];
        setTypeCustomerOptions(options);
      } catch (error) {
        console.error("Error fetching type customers:", error);
      }
    };
    fetchTypeCustomers();
  }, []);

  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      typeCustomer: "",
      names: "",
      surnames: "",
      email: "",
      nit: "",
      dpi: "",
      dateOfBirth: "",
      expirationDateDpi: "",
      marriedLastName: "",
    },

    validationSchema: Yup.object({
      typeCustomer: Yup.string().required("Tipo de cliente requerido"),
      names: Yup.string().required("Nombres son requeridos"),
      surnames: Yup.string().required("Apellidos son requeridos"),
      nit: Yup.string().required("Nit es requerido"),
      email: Yup.string()
        .email("Ingresa un email válido")
        .required("Email es requerido"),
      dpi: Yup.string().required("DPI es requerido"),
      dateOfBirth: Yup.string().required("Fecha de nacimiento es requerida"),
      expirationDateDpi: Yup.string().required("Vencimiento DPI es requerida"),
    }),

    onSubmit: async(values: any) => {
      try {
        const customerData = {
          typeCustomer: values.typeCustomer,
          names: values.names,
          surnames: values.surnames,
          marriedLastName: values.marriedLastName,
          dateOfBirth: values.dateOfBirth,
          dpi: values.dpi,
          nit: values.nit,
          email: values.email,
          expirationDateDpi: values.expirationDateDpi
        };
        const response = await addCustomerList(customerData);
        const customerId = response.data.customerId;
        console.log("Customer created successfully:", response.data.customerId);
        setCustomerId(customerId);
        onSuccess?.();
      } catch (error) {
        console.error("Error creating customer:", error);
      }
    },
  });

  return (
    <React.Fragment>
      <h5 className="mb-3">Información Personal</h5>
      <form onSubmit={validation.handleSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="typeCustomerSelect"
              className="inline-block mb-2 text-base font-medium"
            >
              Tipo de Cliente<span className="text-red-500">*</span>
            </label>
            <Select
              className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              data-choices
              name="typeCustomer"
              options={typeCustomerOptions}
              value={typeCustomerOptions.find(
                option => option.value === validation.values.typeCustomer
              )}
              onChange={(selectedOption: any) => {
                validation.setFieldValue("typeCustomer", selectedOption.value);
              }}
              onBlur={validation.handleBlur}
            />
            {validation.touched.typeCustomer &&
            validation.errors.typeCustomer ? (
              <div className="text-red-500">
                {validation.errors.typeCustomer}
              </div>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="nameInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Nombres <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="names"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese nombres"
              value={validation.values.names}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.names && validation.errors.names ? (
              <div className="text-red-500">{validation.errors.names}</div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="surnamesInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Apellidos <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="surnames"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese apellidos"
              value={validation.values.surnames}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.surnames && validation.errors.surnames ? (
              <div id="surnames" className="text-red-500">
                {validation.errors.surnames}
              </div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="marriedLastNameInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Apellido Casada
            </label>
            <input
              type="text"
              id="marriedLastName"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese apellido de casada"
              value={validation.values.marriedLastName}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
          </div>
          <div>
            <label
              htmlFor="nitInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Nit <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="nit"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese nit"
              value={validation.values.nit}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.nit && validation.errors.nit ? (
              <div className="text-red-500">{validation.errors.nit}</div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="emailInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese email"
              value={validation.values.email}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.email && validation.errors.email ? (
              <div className="text-red-500">{validation.errors.email}</div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="dpiInput"
              className="inline-block mb-2 text-base font-medium"
            >
              DPI <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="dpi"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Ingrese dpi"
              value={validation.values.dpi}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            {validation.touched.dpi && validation.errors.dpi ? (
              <div className="text-red-500">{validation.errors.dpi}</div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="dateOfBirthInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Fecha de nacimiento <span className="text-red-500">*</span>
            </label>
            <FlatpickrComponent
              id="dateOfBirthInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              options={{
                altInput: true,
                altFormat: "d M, Y",
                dateFormat: "Y-m-d"
              }}
              placeholder="Ingrese fecha"
              name="dateOfBirth"
              onChange={(date: any) =>
                validation.setFieldValue(
                  "dateOfBirth",
                  moment(date[0]).format("YYYY-MM-DD")
                )
              }
              value={validation.values.dateOfBirth || ""}
            />
            {validation.touched.dateOfBirth && validation.errors.dateOfBirth ? (
              <div className="text-red-500">
                {validation.errors.dateOfBirth}
              </div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="expirationDateDpiInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Vencimiento DPI <span className="text-red-500">*</span>
            </label>
            <FlatpickrComponent
              id="expirationDateDpiInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              options={{
                altInput: true,
                altFormat: "d M, Y",
                dateFormat: "Y-m-d"
              }}
              placeholder="Ingrese fecha"
              name="expirationDateDpi"
              onChange={(date: any) =>
                validation.setFieldValue(
                  "expirationDateDpi",
                  moment(date[0]).format("YYYY-MM-DD")
                )
              }
              value={validation.values.expirationDateDpi || ""}
            />
            {validation.touched.expirationDateDpi &&
            validation.errors.expirationDateDpi ? (
              <div className="text-red-500">
                {validation.errors.expirationDateDpi}
              </div>
            ) : null}
          </div>
        </div>
      </form>
    </React.Fragment>
  );
});

export default CustomerForm;
